import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { useEffect, type FC } from 'react';
import { MdForum } from 'react-icons/md';
import { useLocation, useSearch } from 'react-location';
// import { useOnlineHotlineAvailable } from '../../modules/datastores/useEntryPoint';
import { createLogger } from '../../modules/prelude/logger';
import shl_content_image from './../../../assets/tos_image.sarnco.jpg';
import { useCreateWebchat } from '../../modules/datastores/useCreateWebchat';
import AdditionalTerms from './AdditionalTerms';
import Cookies from 'universal-cookie';
import { taskIdPattern } from '../../modules/prelude/constants';
import type { UseQueryResult } from 'react-query';
import type { HotlineMetaData } from '../../modules/algebra/global';

const fileLabel = 'TOSTerms2.ts';
const logger = createLogger({
  fileLabel,
});

const TOSTerms: FC<{ statusQuery: UseQueryResult<HotlineMetaData> }> = ({ statusQuery }) => {
  const { endpointTitle, status } = statusQuery?.data || {}
  const location = useLocation();
  const query = useSearch();
  const messaging_service_key = query['messaging_service_key'] as string;
  const { mutateAsync: createWebchat, isLoading } = useCreateWebchat();
  const cookies = new Cookies();
  const startTalking = async () => {
    try {
      const createChat = await createWebchat({messaging_service_key});
      const date = new Date();
      date.setTime(date.getTime() + 10 * 60 * 1000);
      cookies.set(`task_sid${messaging_service_key}`, createChat, { path: '/', expires: date });
      location.history.replace(`/?messaging_service_key=${messaging_service_key}&task_sid=${createChat}`);
    } catch (startChatError) {
      logger.error('Error While Starting Conversation', startChatError);
    }
  };

  useEffect(() => {
    if (taskIdPattern.test(cookies.get(`task_sid${messaging_service_key}`))) {
      location.history.replace(`/?messaging_service_key=${messaging_service_key}&task_sid=${cookies.get(`task_sid${messaging_service_key}`)}`);
    }
  }, []);

  return (
    <div className="bg-pc-1 py-8">
      <div className="px-4 md:px-14">
        <Typography variant="h1" className="text-tc-4 flex justify-center text-2xl font-bold md:text-left text-center">
          {endpointTitle || (`The ${process.env.NX_ORGANIZATION_NAME} Online Hotline`)} 
        </Typography>
        <Typography variant="body1" className="text-xl italic text-center text-tc-1 border-b border-tc-4 pb-3">
          Chat online with a trained staff member who can provide you confidential crisis support.
        </Typography>
      </div>
      <div className="flex flex-col lg:flex-row md:pl-8 sm:pl-0">
        <div className="w-full lg:w-1/2 px-4 md:px-14 py-4">
          <Typography className="text-lg text-tc-1 font-bold">TERMS of SERVICE</Typography>
          <Typography className="text-lg text-tc-1">
            This policy covers the use of the Sexual Assault Response Network of Central Ohio (SARNCO) Helpline and chat feature which can be reached through{' '}
            <Link href="https://www.ohiohealth.com/community-health/sarnco" className="text-tc-4 font-bold py-3 inline-block underline">
              https://www.ohiohealth.com/community-health/sarnco
            </Link>
          </Typography>
          <Typography className="text-lg text-tc-1">
            By visiting our Site, you agree to follow the rules set forth here. If we update our Terms of Use, we will post the updated Terms of Use here. Any such changes will be effective upon
            posting. You should consult the “Terms of Use” bar on this Site each time you visit our Sites. If you do not agree with these Terms of Use, you should not use our sites. If you have any
            questions or suggestions about these Terms of Use, please contact SARNCO at{' '}
            <Link href="tel:6145663000" className="text-tc-4 font-bold py-3 inline-block underline">
              614-566-3000
            </Link>
            , email{' '}
            <Link href="mailto:SARNCO@ohiohealth.com" className="text-tc-4 font-bold py-3 inline-block underline">
              SARNCO@ohiohealth.com
            </Link>
            , or mail to 3830 Olentangy River Rd, Columbus, Ohio, 43214.
          </Typography>

          <Typography className="text-sm text-tc-1 pb-3">
            If You do not agree with the{' '}
            <Link href="https://rainn.org/terms-service" className="text-tc-4 uppercase font-bold py-3 inline-block underline">
              Terms & Conditions
            </Link>
            , you are not authorized to use the Site.
          </Typography>
          <AdditionalTerms />
          {(() => {
            if (statusQuery.isLoading) return <CircularProgress />;
            else if (statusQuery.isSuccess && status)
              return (
                <>
                  <Button startIcon={<MdForum className="w-12 h-12" />} onClick={startTalking} className="chat-now" disabled={isLoading}>
                    Chat Now
                  </Button>

                  <Typography variant="h2" className="text-tc-1 font-bold pt-3 text-lg">
                    By clicking "Chat Now," you are agreeing to the terms.
                  </Typography>
                </>
              );
            else
              return (
                <Typography variant="h2" className="text-tc-1 font-bold pt-3 text-lg">
                  Currently All our Operators Are Busy
                </Typography>
              );
          })()}
        </div>
        <div className="w-full sm:w-1/2 px-4 md:px-14 py-4 mt-2">
          <div
            className="video"
            style={{
              position: 'relative',
              paddingBottom: '56.25%' /* 16:9 */,
              paddingTop: 25,
              height: 0,
            }}
          >
            <img
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
              }}
              src={shl_content_image}
              alt="Some Content"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TOSTerms;
